import React, { PureComponent } from "react";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";

const baseUrl = process.env.REACT_APP_BASE_URL;

/**
 * Public "Verify CFSN Status" page.
 *
 * Answers one question: is this person a registered food scientist with a current, paid licence?
 * On success it shows that person's name and CFSN number and nothing else.
 *
 * Where a search matches several distinct licensed members the backend returns MULTIPLE_MATCHES
 * and no details, and this page asks the searcher to narrow by CFSN. Showing one arbitrary match
 * would let someone checking a particular person be shown a namesake's valid licence.
 */
class VerifyMembership extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      query: "",
      isLoading: false,
      result: null,
    };
  }

  verify = async (event) => {
    if (event) event.preventDefault();

    const query = this.state.query.trim();
    if (!query) {
      this.setState({
        result: {
          outcome: "QUERY_REQUIRED",
          message: "Enter a surname or CFSN number to search.",
        },
      });
      return;
    }

    this.setState({ isLoading: true, result: null });

    try {
      // The CFSN number contains slashes, so it goes in a query parameter. Encoded into a path
      // segment it becomes %2F, which Tomcat rejects with a 400 before Spring ever sees it.
      const response = await fetch(
        `${baseUrl}/api/public/verification/membership?query=${encodeURIComponent(query)}`,
        { method: "GET", headers: { Accept: "application/json" } }
      );

      if (!response.ok) {
        throw new Error(`Verification service returned ${response.status}`);
      }

      this.setState({ result: await response.json(), isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false, result: null });
      Swal.fire({
        title: "Could not complete the check",
        text: `${error.message}. Please try again in a moment.`,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  renderResult() {
    const { result } = this.state;
    if (!result) return null;

    if (result.outcome === "VERIFIED") {
      const { fullName, registrationNumber } = result.member || {};
      return (
        <div className="card my-3 border border-success">
          <div className="card-header pb-3 bg-success">
            <h5 className="text-light text-center mb-0">Verified</h5>
          </div>
          <div className="card-body text-center">
            <p className="text-secondary mb-3">{result.message}</p>
            <h4 className="font-weight-bold text-capitalize mb-2">{fullName}</h4>
            <p className="h5 mb-0">{registrationNumber}</p>
          </div>
        </div>
      );
    }

    const isAmbiguous =
      result.outcome === "MULTIPLE_MATCHES" || result.outcome === "QUERY_REQUIRED";

    return (
      <div className={`card my-3 border ${isAmbiguous ? "border-warning" : "border-danger"}`}>
        <div className={`card-header pb-3 ${isAmbiguous ? "bg-warning" : "bg-danger"}`}>
          <h5 className="text-light text-center mb-0">
            {result.outcome === "MULTIPLE_MATCHES"
              ? "Narrow your search"
              : result.outcome === "QUERY_REQUIRED"
              ? "Search required"
              : "Not verified"}
          </h5>
        </div>
        <div className="card-body text-center">
          <p className="mb-0">{result.message}</p>
        </div>
      </div>
    );
  }

  render() {
    const { isLoading, query } = this.state;

    return (
      <div className="container" style={{ display: "flex", flexDirection: "column" }}>
        <center className="mb-4">
          <img
            src="../assets/images/nicfost.png"
            className="navbar-brand-img"
            style={{ width: 90, height: 90, marginTop: 27, marginBottom: 18 }}
            alt="main_logo"
          />
          <h2 className="text-uppercase">Verify CFSN Status</h2>
          <a href="https://nicfost.gov.ng">Go Back Home</a>
        </center>

        <p className="mt-4 font-weight-bold text-center">
          Enter surname / NiCFoST Registration Number
        </p>

        <center>
          <form
            onSubmit={this.verify}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              width: "50%",
            }}
          >
            <div className="input-group input-group-outline mb-3">
              <input
                type="text"
                className="form-control shadow-none col-lg-8"
                value={query}
                onChange={(e) => this.setState({ query: e.target.value })}
              />
            </div>
            <button
              type="submit"
              style={{ backgroundColor: "#003314" }}
              className="btn btn-success col-lg-4 btn-lg"
              disabled={isLoading}
            >
              {isLoading ? <Spinner animation="border" variant="light" size="sm" /> : "Search"}
            </button>
          </form>
        </center>

        <div className="container" style={{ maxWidth: 640 }}>
          {this.renderResult()}
        </div>
      </div>
    );
  }
}

export default VerifyMembership;
